<template>
  <div class="grow">
    <HandoverCarStatusForm
      v-if="booking && allocation"
      :booking="booking"
      :allocation="allocation"
      :is-saving="isSaving || isLoading"
      :waiting-for-customer="waitingForCustomer"
      @on-submit="handleSubmit"
    />
  </div>
</template>

<script lang="ts" setup>
import type { CarStatusFormValues } from '@/entities/cars/car.entity';
import { useBooking } from '@/queries/use-bookings';
import { useRoute } from 'vue-router';
import HandoverCarStatusForm from './HandoverCarStatusForm.vue';
import { useUpdateCar } from '@/queries/use-cars';
import { computed, ref } from 'vue';
import { useUpdateAllocation } from '@/queries/use-allocations.ts';
import { useProcessNavigation, useProcessSessionContext } from '@/hooks/use-process-step-tracking';
import { useConfirmMilageDeviation } from '@/hooks/use-confirm-milage-deviation';

const route = useRoute();
const { goToNext } = useProcessNavigation();
const { session, isCounterHandover } = useProcessSessionContext();
const { data: booking } = useBooking(route.params.id as string);
const allocation = computed(() => {
  return booking.value?.allocations.find(
    (allocation) => booking.value.allocations[0].id === allocation.id,
  );
});

const { confirmHandover, isLoading } = useConfirmMilageDeviation();
const { isPending: isSavingAllocation, mutateAsync: updateAllocation } = useUpdateAllocation();
const { isPending: isSavingCar, mutateAsync: updateCar } = useUpdateCar();
const isSubmitting = ref(false);

const isSaving = computed(
  () => isSavingAllocation.value || isSavingCar.value || isSubmitting.value,
);

const waitingForCustomer = computed(
  () => isCounterHandover.value && !session.value?.gates?.customerContactConfirmed,
);

const handleSubmit = async (formValues: CarStatusFormValues) => {
  if (!booking.value || !allocation.value) return;
  if (isSubmitting.value) return;
  const { comment, isConfirmed, reason } = await confirmHandover(
    allocation.value,
    formValues.milage,
  );
  if (!isConfirmed) {
    return;
  }
  isSubmitting.value = true;
  try {
    await updateAllocation({
      id: booking.value.allocations[0].id,
      allocation: {
        milageStart: formValues.milage,
        fuelLevelStart: formValues.fuelLevel,
        milageDeviationComment: comment,
        milageDeviationReason: reason,
      },
    });
    await updateCar({
      id: booking.value?.allocations[0].car.id,
      car: {
        fuelLevel: formValues.fuelLevel,
      },
    });
    await goToNext();
  } finally {
    isSubmitting.value = false;
  }
};
</script>
